import { useState, type CSSProperties } from 'react';
import { useAuth } from '../lib/auth';
import {
  addUnit, inviteUrl, linkUnit, newId, rotateInvite, setGroupField, setPersonWeight, updateUnit
} from '../lib/db';
import { sharesLabel, wfmt } from '../lib/money';
import { myUnit, unitWeightTotal } from '../lib/split';
import { Icon } from '../components/Icons';
import { Note, Section, Segmented, Sheet, UnitAvatar, useToast } from '../components/ui';
import { copyText } from '../lib/exports';
import { track } from '../lib/analytics';
import type { SplitBasis, TripData, Unit } from '../types';

/** 1 → ½ → none → 1. Tapping a person cycles their share. */
const nextWeight = (w: number) => (w === 1 ? 0.5 : w === 0.5 ? 0 : 1);

export default function Families({ data, uid }: { data: TripData; uid: string }) {
  const { group, units } = data;
  const toast = useToast();
  const { user } = useAuth();
  const mine = myUnit(units, uid);
  const [editing, setEditing] = useState<Unit | null>(null);
  const [addingHousehold, setAddingHousehold] = useState(false);

  async function share() {
    track('share', { content_type: 'invite', method: 'link' });
    const url = inviteUrl(group.inviteCode);
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
    if (nav.share) {
      try {
        await nav.share({ title: group.name, text: `Join ${group.name} on Evenhouse`, url });
        return;
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
      }
    }
    toast(await copyText(url) ? 'Invite link copied' : url);
  }

  return (
    <>
      {!mine && (
        <div className="banner" style={{ marginTop: 2 }}>
          <Icon name="info" />
          <div>
            <b>Which family are you?</b> Tap a household below to claim it, so your balance
            is tracked with the rest of your family.
          </div>
        </div>
      )}

      <div style={{ marginTop: 10 }}>
        <Note>
          Tap anyone to change how many shares they count for — <b>1</b>, <b>½</b> (older kids)
          or <b>none</b> (little ones). Every split on the trip recalculates instantly.
        </Note>
      </div>

      {units.map((u) => (
        <div className={`card unit-card c${u.color}`} key={u.id}
          style={{ marginTop: 10, '--uc': `var(--u${u.color})` } as CSSProperties}>
          <div className="hd">
            <UnitAvatar unit={u} />
            <span className="nm">
              <span className="t">{u.name}</span>
              <span className="s">
                {u.people.length} member{u.people.length === 1 ? '' : 's'}
                {mine?.id === u.id && ' · your family'}
              </span>
            </span>
            <span className="wchip">{sharesLabel(unitWeightTotal(u))}</span>
          </div>

          <div className="plist">
            {u.people.map((p) => (
              <button
                key={p.id}
                className={`pchip${p.weight === 0 ? ' off' : ''}`}
                onClick={() => void setPersonWeight(group.id, u, p.id, nextWeight(p.weight))}
              >
                <span className="av">{p.name[0]?.toUpperCase()}</span>
                {p.name}
                {p.isChild && p.age ? <span className="muted" style={{ fontWeight: 600 }}>{p.age}</span> : null}
                <span className="w">
                  {p.weight === 0 ? 'not counted' : `${wfmt(p.weight)} share${p.weight === 1 ? '' : 's'}`}
                </span>
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn ghost" style={{ padding: 9, fontSize: 13 }}
              onClick={() => setEditing(u)}>
              Edit family
            </button>
            {mine?.id !== u.id && (
              <button className="btn" style={{ padding: 9, fontSize: 13 }}
                onClick={async () => {
                  await linkUnit(group.id, units, u.id, uid);
                  toast(`You're now part of ${u.name}`);
                }}>
                This is me
              </button>
            )}
          </div>
        </div>
      ))}

      <button className="btn ghost" style={{ marginTop: 12 }}
        onClick={() => setAddingHousehold(true)}>
        Add a family
      </button>

      <Section label="Invite the others" />
      <div className="list">
        <button className="row" onClick={() => void share()}>
          <span className="glyph tint"><Icon name="share" /></span>
          <span className="main">
            <span className="t">Share the invite link</span>
            <span className="s">Anyone with the link can join this trip</span>
          </span>
        </button>
        <button className="row" onClick={async () => {
          await rotateInvite(group);
          track('invite_replaced');
          toast('New link created — the old one no longer works');
        }}>
          <span className="glyph"><Icon name="link" /></span>
          <span className="main">
            <span className="t">Replace the link</span>
            <span className="s">Use this if the old link went somewhere it shouldn't</span>
          </span>
        </button>
      </div>

      <Section label="Default split for new expenses" />
      <Segmented<SplitBasis>
        value={group.defaultBasis}
        onChange={(b) => void setGroupField(group.id, { defaultBasis: b })}
        options={[{ id: 'perPerson', label: 'Per person' }, { id: 'perUnit', label: 'Per family' }]}
      />
      <p className="muted" style={{ fontSize: 12.5, lineHeight: 1.55, margin: '10px 2px 0' }}>
        <b style={{ color: 'var(--ink)' }}>Per person</b> weights each family by how many people
        it brings, so a family of four pays more than someone travelling alone.{' '}
        <b style={{ color: 'var(--ink)' }}>Per family</b> splits evenly between households,
        which is usually what you want for rooms and cabs.
      </p>

      {editing && (
        <EditFamily
          data={data}
          unit={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); toast('Family updated'); }}
        />
      )}

      {addingHousehold && (
        <EditFamily
          data={data}
          unit={null}
          defaultName={units.length === 0 ? user?.displayName ?? '' : ''}
          onClose={() => setAddingHousehold(false)}
          onSaved={() => { setAddingHousehold(false); toast('Family added'); }}
        />
      )}
    </>
  );
}

/* ---------- add / edit one household ---------- */

interface PersonDraft { id: string; name: string; weight: number; isChild: boolean; age: string }

function EditFamily({ data, unit, defaultName, onClose, onSaved }: {
  data: TripData;
  unit: Unit | null;
  defaultName?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { group, units } = data;
  const [name, setName] = useState(unit?.name ?? defaultName ?? '');
  const [people, setPeople] = useState<PersonDraft[]>(
    unit?.people.map((p) => ({
      id: p.id, name: p.name, weight: p.weight,
      isChild: Boolean(p.isChild), age: p.age ? String(p.age) : ''
    })) ?? [{ id: newId(), name: '', weight: 1, isChild: false, age: '' }]
  );
  const [saving, setSaving] = useState(false);

  const valid = name.trim().length > 0 && people.some((p) => p.name.trim());

  function patch(id: string, p: Partial<PersonDraft>) {
    setPeople((ps) => ps.map((x) => (x.id === id ? { ...x, ...p } : x)));
  }

  async function save() {
    if (!valid) return;
    setSaving(true);
    const cleaned = people
      .filter((p) => p.name.trim())
      .map((p) => ({
        id: p.id,
        name: p.name.trim(),
        weight: p.weight,
        isChild: p.isChild,
        age: p.isChild && p.age ? Number(p.age) : null
      }));
    try {
      if (unit) await updateUnit(group.id, unit.id, { name: name.trim(), people: cleaned });
      else await addUnit(group.id, name.trim(), units.length, cleaned);
      onSaved();
    } catch {
      setSaving(false);
    }
  }

  return (
    <Sheet
      title={unit ? 'Edit family' : 'Add a family'}
      onClose={onClose}
      footer={
        <button className="btn" disabled={!valid || saving} onClick={() => void save()}>
          {saving ? 'Saving…' : unit ? 'Save changes' : 'Add family'}
        </button>
      }
    >
      <div className="field">
        <label htmlFor="fam-name">Family name</label>
        <input id="fam-name" className="inp" value={name} autoFocus
          placeholder="Ravi & Priya" onChange={(e) => setName(e.target.value)} />
      </div>

      <div className="field">
        <label>Members</label>
        {people.map((p) => (
          <div className="who-unit" key={p.id}>
            <div className="uh">
              <input
                className="inp" style={{ padding: '8px 11px', fontSize: 14 }}
                value={p.name} placeholder="Name"
                onChange={(e) => patch(p.id, { name: e.target.value })}
              />
              {people.length > 1 && (
                <button className="iconbtn" aria-label={`Remove ${p.name || 'member'}`}
                  onClick={() => setPeople((ps) => ps.filter((x) => x.id !== p.id))}>
                  <Icon name="close" />
                </button>
              )}
            </div>
            <div className="who-people" style={{ alignItems: 'center' }}>
              {[1, 0.5, 0].map((w) => (
                <button key={w} className="ptog" aria-pressed={p.weight === w}
                  onClick={() => patch(p.id, { weight: w })}>
                  {w === 1 ? 'Full share' : w === 0.5 ? 'Half share' : 'Not counted'}
                </button>
              ))}
              <button className="ptog" aria-pressed={p.isChild}
                onClick={() => patch(p.id, { isChild: !p.isChild })}>
                Child
              </button>
              {p.isChild && (
                <input
                  className="inp"
                  style={{ width: 74, padding: '5px 9px', fontSize: 13 }}
                  inputMode="numeric" placeholder="Age" value={p.age}
                  onChange={(e) => patch(p.id, { age: e.target.value.replace(/[^0-9]/g, '') })}
                />
              )}
            </div>
          </div>
        ))}
        <button className="btn ghost" style={{ marginTop: 10 }}
          onClick={() => setPeople((ps) => [
            ...ps, { id: newId(), name: '', weight: 1, isChild: false, age: '' }
          ])}>
          Add a member
        </button>
      </div>

      <div style={{ marginTop: 16 }}>
        <Note>
          A child set to <b>not counted</b> costs the group nothing — whatever they use is
          carried by their own family, which is usually what people mean for toddlers.
        </Note>
      </div>
    </Sheet>
  );
}
